import { PageDto } from '@asset-metrix/portal-gateway-client-js';
import { v4 as uuidV4 } from 'uuid';
import { create } from 'zustand';

import {
  canRemoveWidget,
  flattenContentsToWidgets,
  PAGE_EDIT_DUMMY_WIDGET_ID_PREFIX,
  PageEditWidget,
  sameMoveScope,
} from '../../utils/pageEditModeHelpers';

type PageEditModeState = {
  editModePageId: string | null;
  contentDraft: PageEditWidget[] | null;
  enterPageEditMode: (pageId: string, contents: PageDto['contents']) => void;
  moveContent: (
    pageId: string,
    widgetId: string,
    direction: 'up' | 'down'
  ) => void;
  toggleContentWidth: (pageId: string, widgetId: string) => void;
  duplicateContent: (pageId: string, widgetId: string) => void;
  removeContent: (pageId: string, widgetId: string) => void;
  savePageEdit: () => void;
  exitPageEditMode: () => void;
};

function updateContentDraft(
  state: PageEditModeState,
  pageId: string,
  update: (contentDraft: PageEditWidget[]) => PageEditWidget[]
): PageEditModeState {
  if (state.editModePageId !== pageId || state.contentDraft == null) {
    return state;
  }

  return {
    ...state,
    contentDraft: update(state.contentDraft),
  };
}

export const usePageEditModeStore = create<PageEditModeState>((set) => ({
  editModePageId: null,
  contentDraft: null,
  enterPageEditMode: (pageId, contents) =>
    set({
      editModePageId: pageId,
      contentDraft: flattenContentsToWidgets(contents),
    }),
  moveContent: (pageId, widgetId, direction) =>
    set((state) =>
      updateContentDraft(state, pageId, (contentDraft) => {
        const index = contentDraft.findIndex(
          (item) => item.widget.id === widgetId
        );
        if (index === -1) {
          return contentDraft;
        }

        const current = contentDraft[index];
        if (current == null) {
          return contentDraft;
        }

        const nextIndex = direction === 'up' ? index - 1 : index + 1;
        const neighbor = contentDraft[nextIndex];
        if (neighbor == null || !sameMoveScope(current, neighbor)) {
          return contentDraft;
        }

        const next = [...contentDraft];
        next[index] = neighbor;
        next[nextIndex] = current;
        return next;
      })
    ),
  toggleContentWidth: (pageId, widgetId) =>
    set((state) =>
      updateContentDraft(state, pageId, (contentDraft) =>
        contentDraft.map((item) =>
          item.widget.id !== widgetId
            ? item
            : {
                ...item,
                widthFactor: item.widthFactor <= 0.5 ? 1 : 0.5,
              }
        )
      )
    ),
  duplicateContent: (pageId, widgetId) =>
    set((state) =>
      updateContentDraft(state, pageId, (contentDraft) => {
        const index = contentDraft.findIndex(
          (item) => item.widget.id === widgetId
        );
        if (index === -1) {
          return contentDraft;
        }

        const source = contentDraft[index];
        if (source == null) {
          return contentDraft;
        }

        const next = [...contentDraft];
        next.splice(index + 1, 0, {
          widget: {
            ...structuredClone(source.widget),
            // Dummy prefix → PageEditDummyWidget (no live contentId fetch).
            id: `${PAGE_EDIT_DUMMY_WIDGET_ID_PREFIX}${uuidV4()}`,
            title: `${source.widget.title} (copy)`,
          },
          widthFactor: source.widthFactor,
          tabPlacement: source.tabPlacement,
        });
        return next;
      })
    ),
  removeContent: (pageId, widgetId) =>
    set((state) =>
      updateContentDraft(state, pageId, (contentDraft) => {
        if (!canRemoveWidget(contentDraft, widgetId)) {
          return contentDraft;
        }
        return contentDraft.filter((item) => item.widget.id !== widgetId);
      })
    ),
  savePageEdit: () => {
    // TODO: Persist contentDraft via the page contents update endpoint once backend support lands.
    set({ editModePageId: null, contentDraft: null });
  },
  exitPageEditMode: () => set({ editModePageId: null, contentDraft: null }),
}));

export function useIsPageEditMode(pageId: string | null) {
  return usePageEditModeStore(
    (state) => pageId != null && state.editModePageId === pageId
  );
}
